import { useEffect } from 'react';

import styled from '@emotion/styled';
import { useFormik } from 'formik';
import toast from 'react-hot-toast';
import * as Yup from 'yup';

import { PrimaryBlackButton } from '../../../components/Button/Buttons';
import { Card, CardTitle, Hint, Input, Scroll, SimpleTable } from '../../../components/Layout/PagePieces';
import useChatLimitSettings from '../../../hooks/dashboard/limits/useChatLimitSettings';
import { ChatSettingKey, unitsInUsd } from '../../../models/limits/chatLimits.model';
import { formatNumber, tableFormatDateTimeNG } from '../../../utils/utils';

const LABELS: Record<ChatSettingKey, string> = {
  AGENT_DAILY_BUDGET: 'Daily allowance (units)',
  AGENT_BUYER_BUDGET_MULTIPLIER: 'Buyers get (× allowance)',
  AGENT_BUYER_LOOKBACK_DAYS: 'Counts as a buyer for (days)',
  AGENT_SOFT_LIMIT_RATIO: 'Short replies from (share used)',
};
const KEYS = Object.keys(LABELS) as ChatSettingKey[];

const show = (key: ChatSettingKey, v: number) =>
  key === 'AGENT_SOFT_LIMIT_RATIO' ? `${Math.round(v * 100)}%` : formatNumber(v);

// The chat allowance for every customer (backend app/agent/limits.py). Used
// from the next message; the settings file only holds the defaults. One
// customer's own allowance is set on their customer page.
const ChatLimitsCard = () => {
  const { settings, saving, fetchSettings, save } = useChatLimitSettings();

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line
  }, []);

  const range = (key: ChatSettingKey) => settings?.ranges[key] ?? [0, Infinity];
  const number = (key: ChatSettingKey, whole = true) => {
    const [low, high] = range(key);
    const base = Yup.number().typeError('A number').min(low, `At least ${low}`).max(high, `At most ${formatNumber(high)}`).required('Required');
    return whole ? base.integer('A whole number') : base;
  };
  const schema = Yup.object({
    AGENT_DAILY_BUDGET: number('AGENT_DAILY_BUDGET'),
    AGENT_BUYER_BUDGET_MULTIPLIER: number('AGENT_BUYER_BUDGET_MULTIPLIER'),
    AGENT_BUYER_LOOKBACK_DAYS: number('AGENT_BUYER_LOOKBACK_DAYS'),
    AGENT_SOFT_LIMIT_RATIO: number('AGENT_SOFT_LIMIT_RATIO', false),
    reason: Yup.string().trim().min(10, 'Say why (at least 10 characters)').required('Say why'),
  });

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: schema,
    initialValues: { ...Object.fromEntries(KEYS.map((k) => [k, settings?.values[k] ?? 0])), reason: '' } as Record<string, any>,
    onSubmit: (v) => {
      const changed = Object.fromEntries(KEYS.filter((k) => Number(v[k]) !== settings?.values[k]).map((k) => [k, Number(v[k])]));
      if (!Object.keys(changed).length) {
        toast('Nothing changed');
        return;
      }
      save(changed, v.reason.trim(), () => toast.success('Saved. Customers’ next messages use these.'));
    },
  });

  const price = settings?.usd_per_million_units ?? null;
  const daily = Number(formik.values.AGENT_DAILY_BUDGET) || 0;
  const buyers = daily * (Number(formik.values.AGENT_BUYER_BUDGET_MULTIPLIER) || 0);

  return (
    <Card>
      <CardTitle>Chat limits</CardTitle>
      <Hint>
        How much each customer can chat with the assistant in a day, counted in units (one unit costs what one input token
        costs on {settings?.model ?? 'the chat model'}). At the limit the assistant stops until midnight; keywords, buttons
        and forms keep working. A customer’s own allowance is set on their customer page.
        {settings?.updated_at ? ` Last changed ${tableFormatDateTimeNG(settings.updated_at)} by ${settings.updated_by}.` : ''}
      </Hint>
      <form onSubmit={formik.handleSubmit}>
        <Grid>
          {KEYS.map((key) => (
            <Field key={key}>
              <span>{LABELS[key]}</span>
              <Input
                type='number'
                name={key}
                step={key === 'AGENT_SOFT_LIMIT_RATIO' ? 0.05 : 1}
                value={formik.values[key]}
                onChange={formik.handleChange}
              />
              {formik.errors[key] ? (
                <ErrorText>{String(formik.errors[key])}</ErrorText>
              ) : (
                <small>default {settings ? show(key, settings.file_defaults[key]) : ''}</small>
              )}
            </Field>
          ))}
        </Grid>
        <Hint>
          A customer with no recent purchase gets {formatNumber(daily)} units a day {unitsInUsd(daily, price)}; a recent buyer
          gets {formatNumber(buyers)} {unitsInUsd(buyers, price)}. That’s the most a customer can cost in a day, not what most
          will use.
        </Hint>
        <Field>
          <span>Why are you changing them?</span>
          <Input name='reason' value={formik.values.reason} onChange={formik.handleChange} placeholder='e.g. Haiku 5.5 costs less, so allow more chat' />
          {formik.errors.reason && formik.touched.reason && <ErrorText>{String(formik.errors.reason)}</ErrorText>}
        </Field>
        <Actions>
          <PrimaryBlackButton type='submit' disabled={saving}>
            {saving ? 'Saving…' : 'Save chat limits'}
          </PrimaryBlackButton>
        </Actions>
      </form>
      {!!settings?.changes.length && (
        <Scroll>
          <SimpleTable>
            <thead>
              <tr>
                <th>Changed</th>
                <th>What changed</th>
                <th>Why</th>
                <th>By</th>
              </tr>
            </thead>
            <tbody>
              {settings.changes.map((c, i) => (
                <tr key={i}>
                  <td>{tableFormatDateTimeNG(c.created_at)}</td>
                  <td>
                    {KEYS.filter((k) => c.before?.[k] !== c.after?.[k])
                      .map((k) => `${LABELS[k]}: ${show(k, c.before?.[k])} → ${show(k, c.after?.[k])}`)
                      .join('; ')}
                  </td>
                  <td>{c.reason}</td>
                  <td>{c.who}</td>
                </tr>
              ))}
            </tbody>
          </SimpleTable>
        </Scroll>
      )}
    </Card>
  );
};

export default ChatLimitsCard;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 12px;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.palette.grayTextColor};

  small {
    font-size: 11px;
  }
`;

const ErrorText = styled.span`
  color: ${({ theme }) => theme.palette.errorRed};
  font-size: 12px;
`;

const Actions = styled.div`
  margin-top: 12px;
`;
